/**
 * PATH: apps/quote/src/components/builder/scope-ag-grid/useScopeAgFormulaBar.ts
 * FILE_ID: QB-AGGRID-FORMULA-BAR-HOOK-001
 * PURPOSE: Owns the focused-cell state + sync effects for the SAM-style
 *          formula bar that sits above the grid. Extracted from
 *          ScopeAgGridSection so the section orchestrator stays under
 *          the 300-line cap and the pricelist-picker MR can plug into
 *          the same focused-row data without prop-drilling state.
 * WHY: The formula bar tracks which row + column AG-Grid currently has
 *      focus on. SAM uses the same data to power its pricelist picker
 *      (auto-rate fills into the focused row), Quote Notes shortcuts
 *      (apply template at cursor), and the formula-bar Delete button.
 *      Co-locating the state in a hook means every consumer sees the
 *      same focused cell.
 * FLOW:
 *   ┌────────────────────┐   ┌───────────────────────┐   ┌────────────┐
 *   │ AG-Grid focus      │ → │ handleFocusChange     │ → │ formulaCell │
 *   │ event              │   │  (drops pad rows)     │   │ state       │
 *   └────────────────────┘   └───────────────────────┘   └────────────┘
 *                                                              │
 *                          ┌───────────────────────────────────┘
 *                          ▼
 *           ┌──────────────────────────┐    ┌──────────────┐
 *           │ rows[] reference changes │ -> │ sync effect  │
 *           │  (e.g. paste-range)      │    │ updates value │
 *           └──────────────────────────┘    └──────────────┘
 * RELATED:
 *   - ScopeAgGridSection — consumer
 *   - ScopeAgFormulaBar — renders the bar UI
 *   - pad-row-helpers (isPadRowId)
 */

import { useCallback, useEffect, useState } from 'react'
import type { QuoteLineItem } from '@/lib/quoteApi'
import { isPadRowId } from './pad-row-helpers'

export interface FormulaCellState {
  /** ID of the row currently focused. null when no focus / pad row. */
  itemId: string | null
  /** Column id (field) currently focused. null when no focus / pad row. */
  field: string | null
  /** Stringified value at the focused cell. */
  value: string
}

export interface UseScopeAgFormulaBarArgs {
  rows: QuoteLineItem[]
  isEditable: boolean
  onCellEdit: (itemId: string, field: string, value: unknown) => void
  onDeleteItem: (sectionId: string, itemId: string) => void
  sectionId: string
}

export interface UseScopeAgFormulaBarResult {
  /** Snapshot of the formula bar's focused cell. */
  formulaCell: FormulaCellState
  /** AG-Grid focus event handler — wires into LazyAgGrid.onFocusChange. */
  handleFocusChange: (
    itemId: string | null,
    field: string | null,
    value: string,
  ) => void
  /** Commit a value the user typed into the formula bar's input. */
  commitFormulaEdit: (next: string) => void
  /** Delete the currently focused row (formula bar X button). */
  handleDeleteFocused: () => void
  /** Resolved row object the focused id points at (or null). */
  focusedRow: QuoteLineItem | null
}

export function useScopeAgFormulaBar(
  args: UseScopeAgFormulaBarArgs,
): UseScopeAgFormulaBarResult {
  const { rows, isEditable, onCellEdit, onDeleteItem, sectionId } = args
  const [formulaCell, setFormulaCell] = useState<FormulaCellState>({
    itemId: null,
    field: null,
    value: '',
  })

  const handleFocusChange = useCallback(
    (itemId: string | null, field: string | null, value: string) => {
      // Pads never appear in the formula bar — they have no "Row N"
      // tag and the value is always blank until promoted. Showing them
      // as focused cells confused estimators ("Row undefined · …").
      if (itemId && isPadRowId(itemId)) {
        setFormulaCell({ itemId: null, field: null, value: '' })
        return
      }
      setFormulaCell({ itemId, field, value })
    },
    [],
  )

  const commitFormulaEdit = useCallback(
    (next: string) => {
      if (!formulaCell.itemId || !formulaCell.field) return
      setFormulaCell((s) => ({ ...s, value: next }))
      onCellEdit(formulaCell.itemId, formulaCell.field, next)
    },
    [formulaCell.field, formulaCell.itemId, onCellEdit],
  )

  // Keep formula bar in sync when rows change underneath us (paste-
  // range writes roll values through onUpdateItem before the user's
  // focus has moved). Prefer the raw expression stored in
  // `row.formulas[field]` over the resolved numeric — without this,
  // estimators see "110" in the bar after typing "=qty * 1.1" and
  // lose the ability to edit the formula they just authored.
  useEffect(() => {
    if (!formulaCell.itemId || !formulaCell.field) return
    const row = rows.find((r) => r.id === formulaCell.itemId)
    if (!row) return
    const formulas = (row.formulas ?? {}) as Record<string, string>
    const expression = formulas[formulaCell.field]
    const v = expression
      ? expression
      : String(
          (row as unknown as Record<string, unknown>)[formulaCell.field] ?? '',
        )
    if (v !== formulaCell.value) {
      setFormulaCell((s) => ({ ...s, value: v }))
    }
  }, [rows, formulaCell.itemId, formulaCell.field, formulaCell.value])

  const handleDeleteFocused = useCallback(() => {
    if (!isEditable || !formulaCell.itemId) return
    onDeleteItem(sectionId, formulaCell.itemId)
  }, [isEditable, onDeleteItem, sectionId, formulaCell.itemId])

  const focusedRow = formulaCell.itemId
    ? rows.find((r) => r.id === formulaCell.itemId) ?? null
    : null

  return {
    formulaCell,
    handleFocusChange,
    commitFormulaEdit,
    handleDeleteFocused,
    focusedRow,
  }
}
